import React, { useCallback, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import { lang } from "../../../middleware/lang";
import { GET_ONBOARDING_STATUS, OnboardingStatusData } from "./queries";
import { computeOnboardingStatus, OnboardingStatus } from "./OnboardingGuard";
import {
  PARTNER_LOGO_UPLOAD,
  UPDATE_PARTNER,
  GET_PARTNER,
} from "../account/queries";
import {
  CREATE_CONNECT_ACCOUNT,
  CreateConnectAccountResponse,
} from "../payments/queries";
import { LoaderDots } from "../../../middleware/loaders";
import useSettings from "../../../hooks/settings";

type StepDef = {
  key: keyof OnboardingStatus;
  title: string;
  description: string;
  icon: string;
  optional?: boolean;
};

const STEPS: StepDef[] = [
  {
    key: "hasBranch",
    title: lang("Agrega tu sucursal", "Add a branch"),
    description: lang(
      "Deja que tus clientes sepan en donde te encuentras",
      "Let your customers know where you are",
    ),
    icon: "fa-map-marker-alt",
  },
  {
    key: "hasProducts",
    title: lang("Agrega tus productos", "Add products"),
    description: lang(
      "Agrega al menos un producto de tu menú para comenzar a vender",
      "Add at least one product from your menu to start selling",
    ),
    icon: "fa-utensils",
  },
  {
    key: "hasBrand",
    title: lang("Tu marca", "Your brand"),
    description: lang(
      "Sube tu logo y agrega una descripción corta de tu negocio",
      "Upload your logo and add a short description of your business",
    ),
    icon: "fa-paint-brush",
  },
  {
    key: "hasDeliveryConfig",
    title: lang("Envíos a domicilio", "Delivery"),
    description: lang(
      "Indica si haces envíos a domicilio para que tus clientes lo sepan",
      "Let your customers know if you offer delivery",
    ),
    icon: "fa-motorcycle",
  },
  {
    key: "hasTablesConfig",
    title: lang("Mesas", "Tables"),
    description: lang(
      "Indica si manejas mesas en tu restaurante",
      "Let us know if you use tables at your restaurant",
    ),
    icon: "fa-chair",
  },
  {
    key: "hasPayments",
    title: lang("Configura pagos", "Set up payments"),
    description: lang(
      "Conecta tu cuenta bancaria para recibir pagos de tus clientes",
      "Connect your bank account to receive payments from your customers",
    ),
    icon: "fa-credit-card",
    optional: true,
  },
];

const WelcomePage: React.FC = () => {
  const history = useHistory();
  const { data, loading, refetch } = useQuery<OnboardingStatusData>(
    GET_ONBOARDING_STATUS,
    { fetchPolicy: "network-only" },
  );

  if (loading && !data) {
    return <LoaderDots />;
  }

  if (!data) {
    return null;
  }

  const status = computeOnboardingStatus(data);
  const requiredSteps = STEPS.filter((s) => !s.optional);
  const completedCount = STEPS.filter((s) => status[s.key]).length;
  const requiredComplete = requiredSteps.every((s) => status[s.key]);

  // Find the first incomplete required step, or the first incomplete optional step
  const firstIncompleteRequired = STEPS.findIndex(
    (s) => !s.optional && !status[s.key],
  );
  const currentStepIndex =
    firstIncompleteRequired !== -1
      ? firstIncompleteRequired
      : STEPS.findIndex((s) => !status[s.key]);

  return (
    <main>
      <div className="container" style={{ maxWidth: 700, paddingTop: "2rem" }}>
        <div className="text-center mb-4">
          <h2 style={{ fontWeight: 700 }}>
            {requiredComplete
              ? lang("¡Todo listo!", "All set!")
              : lang(
                  "Bienvenido a Foo para Empresas",
                  "Welcome to Foo for Business",
                )}
          </h2>
          {!requiredComplete && (
            <p className="text-muted">
              {lang(
                "Completa estos pasos para activar tu cuenta",
                "Complete these steps to activate your account",
              )}
            </p>
          )}
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom: "2rem" }}>
          <div className="d-flex justify-content-between mb-1">
            <small className="text-muted">{lang("Progreso", "Progress")}</small>
            <small className="text-muted">
              {completedCount} / {STEPS.length}
            </small>
          </div>
          <div className="progress" style={{ height: 8, borderRadius: 4 }}>
            <div
              className="progress-bar"
              style={{
                width: `${(completedCount / STEPS.length) * 100}%`,
                backgroundColor: requiredComplete ? "#48BB78" : "#FFBB28",
                transition: "width 0.3s",
              }}
            />
          </div>
        </div>

        {requiredComplete && currentStepIndex === -1 ? (
          <div className="text-center">
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: "#48BB78",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1.5rem",
              }}
            >
              <i
                className="fas fa-check"
                style={{ fontSize: 36, color: "#fff" }}
              />
            </div>
            <p style={{ fontSize: 18, marginBottom: "2rem" }}>
              {lang(
                "Tu cuenta está lista. Ya puedes empezar a recibir pedidos.",
                "Your account is ready. You can start receiving orders.",
              )}
            </p>
            <button
              className="btn btn-lg"
              style={{
                background: "#FFBB28",
                color: "#292929",
                fontWeight: 700,
                borderRadius: 8,
                padding: "0.75rem 2rem",
              }}
              onClick={() => history.push("/")}
            >
              {lang("Ir al dashboard", "Go to dashboard")}
            </button>
          </div>
        ) : (
          <>
            {STEPS.map((step, index) => {
              const isComplete = status[step.key];
              const isCurrent = index === currentStepIndex;
              const isLocked =
                !step.optional && index > currentStepIndex;

              return (
                <StepCard
                  key={step.key}
                  step={step}
                  index={index}
                  isComplete={isComplete}
                  isCurrent={isCurrent}
                  isLocked={isLocked}
                  partnerData={data}
                  onRefresh={refetch}
                  onSkip={
                    step.optional && isCurrent && requiredComplete
                      ? () => history.push("/")
                      : undefined
                  }
                />
              );
            })}

            <div className="text-center mt-4 mb-4">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => refetch()}
              >
                <i className="fas fa-sync-alt mr-1" />
                {lang("Verificar progreso", "Check progress")}
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
};

type StepCardProps = {
  step: StepDef;
  index: number;
  isComplete: boolean;
  isCurrent: boolean;
  isLocked: boolean;
  partnerData: OnboardingStatusData;
  onRefresh: () => void;
  onSkip?: () => void;
};

const StepCard: React.FC<StepCardProps> = ({
  step,
  index,
  isComplete,
  isCurrent,
  isLocked,
  partnerData,
  onRefresh,
  onSkip,
}) => {
  const borderColor = isComplete
    ? "#48BB78"
    : isCurrent
      ? "#FFBB28"
      : "#e0e0e0";

  return (
    <div
      className="card mb-3"
      style={{
        borderLeft: `4px solid ${borderColor}`,
        opacity: isLocked ? 0.5 : 1,
      }}
    >
      <div className="card-body">
        <div className="d-flex align-items-center">
          {/* Status icon */}
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: isComplete
                ? "#48BB78"
                : isCurrent
                  ? "#FFBB28"
                  : "#e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 16,
              flexShrink: 0,
            }}
          >
            {isComplete ? (
              <i className="fas fa-check" style={{ color: "#fff" }} />
            ) : (
              <span
                style={{
                  color: isLocked ? "#999" : "#292929",
                  fontWeight: 700,
                }}
              >
                {index + 1}
              </span>
            )}
          </div>

          <div style={{ flex: 1 }}>
            <h5 style={{ margin: 0, fontWeight: 600 }}>
              <i
                className={`fas ${step.icon} mr-2`}
                style={{ color: "#666" }}
              />
              {step.title}
              {step.optional && (
                <span
                  className="badge badge-light ml-2"
                  style={{ fontSize: 10, fontWeight: 500 }}
                >
                  {lang("Opcional", "Optional")}
                </span>
              )}
            </h5>
            <p className="text-muted mb-0" style={{ fontSize: 14 }}>
              {step.description}
            </p>
          </div>

          {isComplete && (
            <span className="badge badge-success" style={{ fontSize: 12 }}>
              <i className="fas fa-check mr-1" />
              {lang("Listo", "Done")}
            </span>
          )}
        </div>

        {/* Expanded content for current step */}
        {isCurrent && !isComplete && (
          <div style={{ marginTop: 16, paddingLeft: 56 }}>
            <StepAction
              stepKey={step.key}
              partnerData={partnerData}
              onRefresh={onRefresh}
            />
            {onSkip && (
              <button
                className="btn btn-link text-muted btn-sm mt-2 p-0"
                onClick={onSkip}
                style={{ fontSize: 13 }}
              >
                {lang("Configurar después", "Set up later")}
                <i className="fas fa-arrow-right ml-1" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

type StepActionProps = {
  stepKey: keyof OnboardingStatus;
  partnerData: OnboardingStatusData;
  onRefresh: () => void;
};

const StepAction: React.FC<StepActionProps> = ({
  stepKey,
  partnerData,
  onRefresh,
}) => {
  switch (stepKey) {
    case "hasBranch":
      return <BranchAction />;
    case "hasProducts":
      return <ProductAction />;
    case "hasBrand":
      return <BrandAction partnerData={partnerData} onRefresh={onRefresh} />;
    case "hasDeliveryConfig":
      return <DeliveryAction onRefresh={onRefresh} />;
    case "hasTablesConfig":
      return <TablesAction onRefresh={onRefresh} />;
    case "hasPayments":
      return <PaymentAction />;
    default:
      return null;
  }
};

const BranchAction: React.FC = () => (
  <div>
    <Link
      to="/branches/create"
      className="btn"
      style={{
        background: "#FFBB28",
        color: "#292929",
        fontWeight: 600,
        borderRadius: 8,
      }}
    >
      <i className="fas fa-plus mr-2" />
      {lang("Crear sucursal", "Create branch")}
    </Link>
  </div>
);

const ProductAction: React.FC = () => (
  <div>
    <Link
      to="/products/new"
      className="btn"
      style={{
        background: "#FFBB28",
        color: "#292929",
        fontWeight: 600,
        borderRadius: 8,
      }}
    >
      <i className="fas fa-plus mr-2" />
      {lang("Agregar producto", "Add product")}
    </Link>
  </div>
);

const BrandAction: React.FC<{
  partnerData: OnboardingStatusData;
  onRefresh: () => void;
}> = ({ partnerData, onRefresh }) => {
  const hasLogo = partnerData.getPartner.logo !== null;
  const hasDesc = partnerData.getPartner.short_desc !== null;
  const [shortDesc, setShortDesc] = useState(
    partnerData.getPartner.short_desc || "",
  );
  const [descSaved, setDescSaved] = useState(false);

  const [uploadLogo, { loading: uploading }] = useMutation(
    PARTNER_LOGO_UPLOAD,
    {
      refetchQueries: [
        { query: GET_PARTNER },
        { query: GET_ONBOARDING_STATUS },
      ],
      onCompleted: () => {
        toast.success(lang("Logo subido", "Logo uploaded"));
        onRefresh();
      },
    },
  );

  const [updatePartner, { loading: saving }] = useMutation(UPDATE_PARTNER, {
    refetchQueries: [{ query: GET_PARTNER }, { query: GET_ONBOARDING_STATUS }],
    onCompleted: () => {
      setDescSaved(true);
      toast.success(lang("Descripción guardada", "Description saved"));
      onRefresh();
    },
  });

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        uploadLogo({ variables: { file: acceptedFiles[0] } });
      }
    },
    [uploadLogo],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
    accept: "image/png,image/jpeg",
    disabled: uploading,
  });

  const handleSaveDesc = async () => {
    if (!shortDesc.trim()) return;
    try {
      await updatePartner({ variables: { short_desc: shortDesc } });
    } catch {
      toast.error(lang("Error al guardar", "Error saving"));
    }
  };

  return (
    <div>
      {/* Logo upload */}
      <div className="mb-3">
        <label style={{ fontWeight: 600, fontSize: 14 }}>
          {lang("Logo", "Logo")}{" "}
          {hasLogo && <i className="fas fa-check-circle text-success ml-1" />}
        </label>
        <div
          {...getRootProps()}
          style={{
            border: "2px dashed #ddd",
            borderRadius: 8,
            padding: "1rem",
            textAlign: "center",
            cursor: "pointer",
            background: "#f8f9fa",
          }}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <div>
              <i className="fas fa-spinner fa-spin fa-lg" />
              <p className="mb-0 mt-1 text-muted" style={{ fontSize: 13 }}>
                {lang("Subiendo...", "Uploading...")}
              </p>
            </div>
          ) : (
            <div>
              <i
                className="fas fa-cloud-upload-alt fa-lg"
                style={{ color: "#999" }}
              />
              <p className="mb-0 mt-1" style={{ fontSize: 13 }}>
                {hasLogo
                  ? lang("Click para cambiar logo", "Click to change logo")
                  : lang(
                      "Click o arrastra tu logo (PNG, JPG)",
                      "Click or drag your logo (PNG, JPG)",
                    )}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Short description */}
      <div>
        <label style={{ fontWeight: 600, fontSize: 14 }}>
          {lang("Descripción corta", "Short description")}{" "}
          {hasDesc && <i className="fas fa-check-circle text-success ml-1" />}
        </label>
        <div className="input-group">
          <textarea
            className="form-control"
            rows={2}
            value={shortDesc}
            onChange={(e) => {
              setShortDesc(e.target.value);
              setDescSaved(false);
            }}
            placeholder={lang(
              "Ej: Tacos al pastor y más...",
              "E.g.: Pastor tacos and more...",
            )}
          />
        </div>
        <button
          className="btn btn-sm mt-2"
          style={{
            background: "#FFBB28",
            color: "#292929",
            fontWeight: 600,
            borderRadius: 6,
          }}
          onClick={handleSaveDesc}
          disabled={saving || !shortDesc.trim() || descSaved}
        >
          {saving ? (
            <span className="spinner-border spinner-border-sm mr-1" />
          ) : null}
          {descSaved ? lang("Guardado", "Saved") : lang("Guardar", "Save")}
        </button>
      </div>
    </div>
  );
};

const DeliveryAction: React.FC<{ onRefresh: () => void }> = ({ onRefresh }) => {
  const history = useHistory();
  const [, saveSettings] = useSettings();

  const handleChoice = async (makesDelivery: boolean) => {
    await saveSettings("disable_delivery", !makesDelivery);
    if (makesDelivery) {
      history.push("/account/settings/delivery");
    } else {
      onRefresh();
    }
  };

  return (
    <div>
      <p className="text-muted mb-2" style={{ fontSize: 13 }}>
        {lang(
          "¿Haces envíos a domicilio desde tu restaurante?",
          "Do you offer delivery from your restaurant?",
        )}
      </p>
      <div className="d-flex" style={{ gap: 12 }}>
        <button
          className="btn"
          style={{
            background: "#FFBB28",
            color: "#292929",
            fontWeight: 600,
            borderRadius: 8,
          }}
          onClick={() => handleChoice(true)}
        >
          <i className="fas fa-check mr-2" />
          {lang("Sí, hago envíos", "Yes, I deliver")}
        </button>
        <button
          className="btn btn-outline-secondary"
          style={{
            fontWeight: 600,
            borderRadius: 8,
          }}
          onClick={() => handleChoice(false)}
        >
          <i className="fas fa-times mr-2" />
          {lang("No hago envíos", "No delivery")}
        </button>
      </div>
    </div>
  );
};

const TablesAction: React.FC<{ onRefresh: () => void }> = ({ onRefresh }) => {
  const history = useHistory();
  const [, saveSettings] = useSettings();

  const handleChoice = async (hasTables: boolean) => {
    await saveSettings("has_tables", hasTables);
    if (hasTables) {
      history.push("/account/settings/tables");
    } else {
      onRefresh();
    }
  };

  return (
    <div>
      <p className="text-muted mb-2" style={{ fontSize: 13 }}>
        {lang(
          "¿Manejas mesas en tu restaurante?",
          "Do you use tables at your restaurant?",
        )}
      </p>
      <div className="d-flex" style={{ gap: 12 }}>
        <button
          className="btn"
          style={{
            background: "#FFBB28",
            color: "#292929",
            fontWeight: 600,
            borderRadius: 8,
          }}
          onClick={() => handleChoice(true)}
        >
          <i className="fas fa-check mr-2" />
          {lang("Sí, manejo mesas", "Yes, I use tables")}
        </button>
        <button
          className="btn btn-outline-secondary"
          style={{
            fontWeight: 600,
            borderRadius: 8,
          }}
          onClick={() => handleChoice(false)}
        >
          <i className="fas fa-times mr-2" />
          {lang("No manejo mesas", "No tables")}
        </button>
      </div>
    </div>
  );
};

const PaymentAction: React.FC = () => {
  const [createAccount, { loading }] =
    useMutation<CreateConnectAccountResponse>(CREATE_CONNECT_ACCOUNT);

  const handleConnect = async () => {
    try {
      const { data } = await createAccount();
      if (data?.createConnectAccount.url) {
        window.location.href = data.createConnectAccount.url;
      }
    } catch (e: any) {
      toast.error(
        e.message ||
          lang("Error al conectar cuenta", "Error connecting account"),
      );
    }
  };

  return (
    <div>
      <p className="text-muted" style={{ fontSize: 13 }}>
        {lang(
          "Serás redirigido a Stripe para conectar tu cuenta bancaria.",
          "You'll be redirected to Stripe to connect your bank account.",
        )}
      </p>
      <button
        className="btn"
        style={{
          background: "#FFBB28",
          color: "#292929",
          fontWeight: 600,
          borderRadius: 8,
        }}
        onClick={handleConnect}
        disabled={loading}
      >
        {loading ? (
          <span className="spinner-border spinner-border-sm mr-2" />
        ) : (
          <i className="fas fa-link mr-2" />
        )}
        {lang("Conectar cuenta bancaria", "Connect bank account")}
      </button>
    </div>
  );
};

export default WelcomePage;
